import { useEffect, useRef, useState } from "react";
import { AnimatePresence, m, useReducedMotion } from "motion/react";
import type { AssessmentAnswers, QuestionDefinition } from "../types/friction-finder";
import { validateQuestionAnswer } from "../lib/validation";
import { Button } from "./Button";
import { ProgressIndicator } from "./ProgressIndicator";

type Props = {
  question: QuestionDefinition;
  questionNumber: number;
  answers: AssessmentAnswers;
  onChange: (answers: AssessmentAnswers) => void;
  onBack?: () => void;
  onContinue: () => void;
  onRestart: () => void;
};

export function QuestionScreen({ question, questionNumber, answers, onChange, onBack, onContinue, onRestart }: Props) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [interactionError, setInteractionError] = useState<{ questionId: string; message: string } | null>(null);
  const [customTouchedQuestion, setCustomTouchedQuestion] = useState<string | null>(null);
  const selected = answers[question.id];
  const validationError = validateQuestionAnswer(question.id, answers);
  const isValid = !validationError;
  const reduceMotion = useReducedMotion();
  const customValue = question.id === "primary_friction" ? answers.customFriction ?? "" : question.id === "desired_outcomes" ? answers.customOutcome ?? "" : "";
  const hasCustomSelection = question.id === "primary_friction"
    ? selected.includes("other_friction")
    : question.id === "desired_outcomes" && selected.includes("other_outcome");
  const shouldShowValidationError = !isValid && selected.length > 0 && (!hasCustomSelection || customValue.trim().length > 0 || customTouchedQuestion === question.id);
  const displayedError = interactionError?.questionId === question.id
    ? interactionError.message
    : shouldShowValidationError ? validationError : null;
  const transition = { duration: reduceMotion ? 0 : 0.18, ease: [0.22, 1, 0.36, 1] as const };

  useEffect(() => {
    headingRef.current?.focus();
  }, [question.id]);

  const select = (id: string) => {
    setInteractionError(null);
    if (question.type === "single") {
      onChange({ ...answers, [question.id]: [id] });
      return;
    }
    if (question.id === "affected_groups") {
      if (id === "everyone") {
        onChange({ ...answers, affected_groups: selected.includes("everyone") ? [] : ["everyone"] });
        return;
      }
      const withoutEveryone = selected.filter((value) => value !== "everyone");
      if (withoutEveryone.includes(id)) {
        onChange({ ...answers, affected_groups: withoutEveryone.filter((value) => value !== id) });
        return;
      }
      if (withoutEveryone.length >= question.max) {
        const message = question.validation.over;
        if (message) setInteractionError({ questionId: question.id, message });
        return;
      }
      onChange({ ...answers, affected_groups: [...withoutEveryone, id] });
      return;
    }
    if (selected.includes(id)) {
      onChange({ ...answers, [question.id]: selected.filter((value) => value !== id) });
    } else if (selected.length >= question.max) {
      const message = question.validation.over;
      if (message) setInteractionError({ questionId: question.id, message });
    } else {
      onChange({ ...answers, [question.id]: [...selected, id] });
    }
  };

  const describedBy = `${question.id}-instruction ${displayedError ? `${question.id}-error` : ""}`.trim();
  return (
    <main className="content-shell question-screen">
      <ProgressIndicator current={questionNumber} />
      <m.section
        key={question.id}
        data-motion-region="question"
        aria-labelledby={`${question.id}-heading`}
        aria-describedby={describedBy}
        initial={reduceMotion ? false : { opacity: 0.72, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transition}
      >
        <h1 ref={headingRef} tabIndex={-1} id={`${question.id}-heading`}>{question.heading}</h1>
        <p className="lead" id={`${question.id}-instruction`}>{question.instruction}</p>
        {question.type === "multi" && <p className="selection-count" aria-live="polite">{selected.length} of 3 selected</p>}
        <div className="answer-list" role={question.type === "single" ? "radiogroup" : "group"} aria-label={question.heading}>
          {question.options.map((option) => {
            const isSelected = selected.includes(option.id);
            return (
              <m.button
                key={option.id}
                type="button"
                className={`answer-card ${isSelected ? "answer-card--selected" : ""}`}
                role={question.type === "single" ? "radio" : "checkbox"}
                aria-checked={isSelected}
                onClick={() => select(option.id)}
                animate={{ scale: isSelected && !reduceMotion ? 1.004 : 1 }}
                whileHover={reduceMotion ? undefined : { y: -1 }}
                whileTap={reduceMotion ? undefined : { scale: 0.992 }}
                transition={transition}
              >
                <m.span className="answer-indicator" aria-hidden="true" animate={{ scale: isSelected && !reduceMotion ? 1.04 : 1 }} transition={transition}>{isSelected ? "✓" : ""}</m.span>
                <span>
                  <span className="answer-label">{option.label}</span>
                  {option.description && <span className="answer-description">{option.description}</span>}
                </span>
              </m.button>
            );
          })}
        </div>

        <AnimatePresence initial={false}>
        {question.id === "primary_friction" && selected.includes("other_friction") && (
          <m.div className="field custom-field" initial={reduceMotion ? false : { opacity: 0.7, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={reduceMotion ? undefined : { opacity: 0, y: -3 }} transition={transition}>
            <label htmlFor="custom-friction">Describe the main friction</label>
            <textarea
              id="custom-friction"
              rows={3}
              minLength={10}
              maxLength={160}
              placeholder="Briefly describe what is making the business harder to run"
              value={answers.customFriction ?? ""}
              aria-describedby={`custom-friction-help${displayedError ? ` ${question.id}-error` : ""}`}
              aria-invalid={((answers.customFriction ?? "").trim().length > 0 || customTouchedQuestion === question.id) && (answers.customFriction ?? "").trim().length < 10}
              onChange={(event) => onChange({ ...answers, customFriction: event.target.value })}
              onBlur={() => setCustomTouchedQuestion(question.id)}
            />
            <p className="field-help" id="custom-friction-help">10 to 160 characters · {(answers.customFriction ?? "").length}/160</p>
          </m.div>
        )}
        </AnimatePresence>

        <AnimatePresence initial={false}>
        {question.id === "desired_outcomes" && selected.includes("other_outcome") && (
          <m.div className="field custom-field" initial={reduceMotion ? false : { opacity: 0.7, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={reduceMotion ? undefined : { opacity: 0, y: -3 }} transition={transition}>
            <label htmlFor="custom-outcome">Describe what better would look like</label>
            <textarea
              id="custom-outcome"
              rows={3}
              minLength={5}
              maxLength={120}
              placeholder="Briefly describe the outcome you want"
              value={answers.customOutcome ?? ""}
              aria-describedby={`custom-outcome-help${displayedError ? ` ${question.id}-error` : ""}`}
              aria-invalid={((answers.customOutcome ?? "").trim().length > 0 || customTouchedQuestion === question.id) && (answers.customOutcome ?? "").trim().length < 5}
              onChange={(event) => onChange({ ...answers, customOutcome: event.target.value })}
              onBlur={() => setCustomTouchedQuestion(question.id)}
            />
            <p className="field-help" id="custom-outcome-help">5 to 120 characters · {(answers.customOutcome ?? "").length}/120</p>
          </m.div>
        )}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {displayedError && (
            <m.p
              key={displayedError}
              className="validation-message"
              id={`${question.id}-error`}
              role="alert"
              initial={reduceMotion ? false : { opacity: 0.01, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: -2 }}
              transition={{ ...transition, duration: reduceMotion ? 0 : 0.14 }}
            >{displayedError}</m.p>
          )}
        </AnimatePresence>
      </m.section>
      <div className="question-actions">
        <div>{onBack && <Button variant="secondary" onClick={onBack}>Back</Button>}</div>
        <Button onClick={onContinue} disabled={!isValid} aria-describedby={!isValid ? `${question.id}-instruction` : undefined}>Continue</Button>
      </div>
      <button className="restart-link" type="button" onClick={onRestart}>Start again</button>
      <div className="sr-only" aria-live="polite">{question.heading}</div>
    </main>
  );
}
